// src/pages/Unauthorized/UnauthorizedPage.jsx
import React from "react";
import { useNavigate } from "react-router-dom";
import {
  FaArrowLeft,
  FaExclamationTriangle,
  FaHome,
  FaLock,
  FaShieldAlt,
  FaSignInAlt,
  FaTrophy,
} from "react-icons/fa";

import useScrollToTop from "../../hooks/useScrollToTop";
import ScrollIndicator from "../../hooks/ScrollIndicator";

const PAGE =
  "min-h-screen bg-slate-50 text-slate-900 dark:bg-slate-950 dark:text-slate-50";

export default function UnauthorizedPage() {
  useScrollToTop();

  const navigate = useNavigate();

  const goBack = () => {
    if (window.history.length > 1) {
      navigate(-1);
      return;
    }

    navigate("/");
  };

  return (
    <main className={PAGE}>
      <ScrollIndicator />

      <div className="relative flex min-h-screen items-center justify-center overflow-hidden px-4 py-10 pt-[120px] sm:px-6 md:pt-[145px]">
        <div className="absolute inset-0 bg-[radial-gradient(circle_at_top_left,rgba(79,70,229,0.18),transparent_34%),radial-gradient(circle_at_bottom_right,rgba(236,72,153,0.16),transparent_34%)]" />
        <div className="absolute -right-24 top-20 h-72 w-72 rounded-full bg-indigo-500/20 blur-3xl" />
        <div className="absolute -bottom-24 -left-24 h-72 w-72 rounded-full bg-pink-500/20 blur-3xl" />

        <section className="relative w-full max-w-3xl overflow-hidden rounded-[36px] border border-slate-200 bg-white shadow-2xl dark:border-slate-800 dark:bg-slate-900">
          <div className="relative overflow-hidden bg-slate-950 px-6 py-8 text-white sm:px-8 sm:py-10">
            <div className="absolute -right-16 -top-16 h-48 w-48 rounded-full bg-red-500/25 blur-3xl" />
            <div className="absolute -bottom-20 left-1/3 h-48 w-48 rounded-full bg-indigo-500/25 blur-3xl" />

            <div className="relative text-center">
              <div className="mx-auto mb-5 flex h-20 w-20 items-center justify-center rounded-[28px] border border-white/15 bg-white/10 text-3xl shadow-lg backdrop-blur">
                <FaLock />
              </div>

              <div className="mb-4 inline-flex items-center gap-2 rounded-full border border-white/15 bg-white/10 px-3 py-1.5 text-xs font-bold uppercase tracking-[0.18em] text-white/80">
                <FaShieldAlt />
                Accès protégé
              </div>

              <h1 className="text-3xl font-bold tracking-tight sm:text-5xl">
                Accès refusé
              </h1>

              <p className="mx-auto mt-4 max-w-2xl text-sm leading-relaxed text-slate-300 sm:text-base">
                Vous n’avez pas les autorisations nécessaires pour consulter
                cette page. Cette zone est réservée à certains rôles ou à une
                formule active.
              </p>
            </div>
          </div>

          <div className="p-5 sm:p-7">
            <div className="grid grid-cols-1 gap-3 sm:grid-cols-3">
              <InfoCard
                icon={FaExclamationTriangle}
                title="Permission requise"
                text="Votre compte ne possède pas le rôle nécessaire."
              />

              <InfoCard
                icon={FaTrophy}
                title="Formule limitée"
                text="Certaines pages nécessitent une formule active."
              />

              <InfoCard
                icon={FaShieldAlt}
                title="Accès sécurisé"
                text="Les droits protègent les données des compétitions."
              />
            </div>

            <div className="mt-6 rounded-[26px] border border-amber-200 bg-amber-50 p-4 text-sm leading-relaxed text-amber-800 dark:border-amber-900/60 dark:bg-amber-950/40 dark:text-amber-300">
              Si vous pensez qu’il s’agit d’une erreur, contactez
              l’organisateur ou l’administrateur de l’application afin de
              vérifier votre rôle ou votre formule.
            </div>

            <div className="mt-6 grid grid-cols-1 gap-3 sm:grid-cols-3">
              <button
                type="button"
                onClick={goBack}
                className="inline-flex items-center justify-center gap-2 rounded-2xl bg-slate-950 px-4 py-3 text-sm font-bold text-white transition hover:-translate-y-0.5 hover:bg-slate-800 dark:bg-white dark:text-slate-950 dark:hover:bg-slate-100"
              >
                <FaArrowLeft />
                Retour
              </button>

              <button
                type="button"
                onClick={() => navigate("/")}
                className="inline-flex items-center justify-center gap-2 rounded-2xl bg-slate-100 px-4 py-3 text-sm font-bold text-slate-700 transition hover:-translate-y-0.5 hover:bg-slate-200 dark:bg-slate-800 dark:text-slate-100 dark:hover:bg-slate-700"
              >
                <FaHome />
                Accueil
              </button>

              <button
                type="button"
                onClick={() => navigate("/login")}
                className="inline-flex items-center justify-center gap-2 rounded-2xl bg-gradient-to-r from-indigo-600 to-pink-500 px-4 py-3 text-sm font-bold text-white shadow-lg transition hover:-translate-y-0.5 hover:shadow-xl"
              >
                <FaSignInAlt />
                Connexion
              </button>
            </div>
          </div>
        </section>
      </div>
    </main>
  );
}

function InfoCard({ icon: Icon, title, text }) {
  return (
    <article className="rounded-[24px] border border-slate-200 bg-slate-50 p-4 dark:border-slate-800 dark:bg-slate-950/60">
      <div className="mb-3 flex h-11 w-11 items-center justify-center rounded-2xl bg-indigo-50 text-indigo-600 dark:bg-indigo-950/70 dark:text-indigo-300">
        <Icon />
      </div>

      <h2 className="font-bold text-slate-900 dark:text-white">{title}</h2>

      <p className="mt-1 text-sm leading-relaxed text-slate-500 dark:text-slate-400">
        {text}
      </p>
    </article>
  );
}